import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from 'react-helmet-async';
import "./fonts.css";
import "@/index.css";
import App from "@/App";
import ErrorBoundary from "@/components/ErrorBoundary";
import { reportWebVitals } from './reportWebVitals';
import { registerSW } from './serviceWorkerRegistration';

// Sentry — opt-in via REACT_APP_SENTRY_DSN env var at build time.
// If unset → silent no-op (no SDK init, no network calls, no bundle penalty).
// Loaded via DYNAMIC import so @sentry/react is CODE-SPLIT out of the main
// bundle: when no DSN ships (the current prod case) the SDK is never in the
// initial payload at all — it's fetched only when a DSN is present. This keeps
// main.js lean for mobile TBT/parse. Mirrors the backend's SENTRY_DSN pattern.
//
// To activate:
//   REACT_APP_SENTRY_DSN=https://...@sentry.io/... npm run build
//
// Capture excludes:
//   - 4xx HTTP errors (those are user-input issues, not bugs)
//   - Browser-extension noise (ResizeObserver loop, etc.)
const _sentryDsn = process.env.REACT_APP_SENTRY_DSN;
if (_sentryDsn) {
  import("@sentry/react").then((Sentry) => {
    Sentry.init({
      dsn: _sentryDsn,
      environment: process.env.NODE_ENV || "production",
      release: process.env.REACT_APP_VERSION || undefined,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0.01,
      sendDefaultPii: false,
      beforeSend(event, hint) {
        // Filter 4xx HTTP errors — not bugs
        const err = hint?.originalException;
        if (err && err.response && err.response.status >= 400 && err.response.status < 500) {
          return null;
        }
        // Filter browser-extension noise
        const msg = event?.message || (event?.exception?.values?.[0]?.value) || "";
        if (/ResizeObserver loop|Non-Error promise rejection captured/i.test(msg)) {
          return null;
        }
        return event;
      },
    });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);

// Measure and report web vitals for performance monitoring
reportWebVitals();

// Register service worker for offline support, caching, and push notifications.
// Runs after render to not block the initial paint.
registerSW();
